import { useEffect } from 'preact/hooks';
import emitter from '@webcore/core/events/eventemitter';
import { IS_EYEJACK_APP } from '@webcore/core/utils/utils';
import { useXRAnalyticsSession } from './useXRAnalyticsSession';

export interface AppAnalyticsConfig {
  measurementId: string;
  debug?: boolean;
}

/**
 * App-level analytics setup: init, page_view, and emitter forwarding.
 * Use this at the app root; returns sendEvent for AR session and other events.
 */
export function useAppAnalytics(config: AppAnalyticsConfig) {
  const { initAnalytics, sendEvent, isUserIdLoading } = useXRAnalyticsSession({
    measurementId: config.measurementId,
    debug: config.debug ?? import.meta.env.DEV,
  });

  // Initialize analytics when userId is ready
  useEffect(() => {
    if (isUserIdLoading) return;
    if (import.meta.env.DEV) return;
    initAnalytics().then(() => {
      // Only send page_view on website, not in App Clip
      if (!IS_EYEJACK_APP) {
        sendEvent('page_view', {
          page_path: window.location.pathname,
          page_title: document.title,
        });
      }
    }).catch(() => {
      console.warn('[AppAnalytics] Initialization failed');
    });
  }, [isUserIdLoading, initAnalytics, sendEvent]);

  // Forward web-core events to GA4
  useEffect(() => {
    const handleAnalyticsEvent = (event: { name: string; params?: Record<string, any> }) => {
      sendEvent(event.name, event.params);
    };
    const handleXrErrorUndefined = () => sendEvent('ar_error', { error_type: 'undefined' });
    const handleXrErrorNotSupported = () => sendEvent('ar_error', { error_type: 'not_supported' });
    const handleXrErrorPermissionDenied = (data: { type?: string }) =>
      sendEvent('ar_error', { error_type: 'permission_denied', permission_type: data?.type });

    emitter.on('analytics', handleAnalyticsEvent);
    emitter.on('xr-error-undefined', handleXrErrorUndefined);
    emitter.on('xr-error-not-supported', handleXrErrorNotSupported);
    emitter.on('xr-error-permission-denied', handleXrErrorPermissionDenied);

    return () => {
      emitter.off('analytics', handleAnalyticsEvent);
      emitter.off('xr-error-undefined', handleXrErrorUndefined);
      emitter.off('xr-error-not-supported', handleXrErrorNotSupported);
      emitter.off('xr-error-permission-denied', handleXrErrorPermissionDenied);
    };
  }, [sendEvent]);

  return { sendEvent };
}
